import axios from 'axios';
import { setRequestId, captureException, addBreadcrumb } from '@/lib/sentry';
import { toast } from 'sonner';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
});

// Track shown error messages to avoid duplicate toasts
let lastErrorMessage = '';
let lastErrorTime = 0;

// User-friendly error messages
const getErrorMessage = (error) => {
  const status = error.response?.status;
  const detail = error.response?.data?.detail;
  
  // Use backend error message if available
  if (detail && typeof detail === 'string') {
    return detail;
  }
  
  // Status-based messages
  switch (status) {
    case 400:
      return 'Invalid request. Please check your input.';
    case 403:
      return 'You don\'t have permission to do this.';
    case 404:
      return 'The requested item was not found.';
    case 409:
      return 'Conflict - this action cannot be completed.';
    case 422:
      return 'Invalid data. Please check your input.';
    case 429:
      return 'Too many requests. Please wait a moment.';
    case 500:
      return 'Server error. Please try again later.';
    case 502:
    case 503:
    case 504:
      return 'Service temporarily unavailable. Please try again.';
    default:
      if (!error.response) {
        return 'Network error. Please check your connection.';
      }
      return 'Something went wrong. Please try again.';
  }
};

// Request interceptor - add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('mm_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  
  // Add breadcrumb for debugging
  addBreadcrumb(`API Request: ${config.method?.toUpperCase()} ${config.url}`, 'http');
  
  return config;
});

// Response interceptor - capture request_id and handle errors
api.interceptors.response.use(
  (response) => {
    // Capture request_id from backend for Sentry correlation
    const requestId = response.headers['x-request-id'];
    if (requestId) {
      setRequestId(requestId);
      // Store for potential error display
      response.requestId = requestId;
    }
    return response;
  },
  (err) => {
    // Capture request_id even on error
    const requestId = err.response?.headers?.['x-request-id'];
    if (requestId) {
      setRequestId(requestId);
      err.requestId = requestId;
    }
    
    // Handle 401 - unauthorized (silent, will redirect)
    if (err.response?.status === 401) {
      localStorage.removeItem('mm_token');
      localStorage.removeItem('mm_user');
      window.location.href = '/login';
      return Promise.reject(err);
    }
    
    // Show toast for errors (avoid duplicate toasts)
    const errorMessage = getErrorMessage(err);
    const now = Date.now();
    
    // Only show toast if it's a different message or 3+ seconds have passed
    if (errorMessage !== lastErrorMessage || now - lastErrorTime > 3000) {
      // Don't show toasts for certain silent errors
      const silentErrors = [
        'SESSION_LIMIT_EXCEEDED', // Handled by login page modal
        'ALREADY_EXISTS', // Often handled by form validation
      ];
      
      // Code can be at root level OR nested inside detail (e.g. SESSION_LIMIT_EXCEEDED)
      const errorCode = err.response?.data?.code || err.response?.data?.detail?.code;
      if (!silentErrors.includes(errorCode)) {
        toast.error(errorMessage);
        lastErrorMessage = errorMessage;
        lastErrorTime = now;
      }
    }
    
    // Capture 5xx errors to Sentry
    if (err.response?.status >= 500) {
      captureException(err, {
        request_id: requestId,
        endpoint: err.config?.url,
        status: err.response?.status,
      });
    }
    
    return Promise.reject(err);
  }
);

export default api;
